import { FeatureOption, FilterKeys, Filters, Option } from '@utils/pageConfig/Types';

import { buildOptions, isDataTypeFeature } from '@utils/hook/selector/ShowPerformanceWithinSelector';

export const initialSegmentByValue: FeatureOption = {
  label: '',
  value: '',
  dataType: '',
  feature: '',
  filterKey: FilterKeys.ADDFEAT,
};

/**
 * StackedChart By filter selector, first feature as default if available.
 *
 * If feature selected and change pg, apply side filter, then change to new pg first feature.
 *
 * If non feature selected and change PG, apply side filter, then no change.
 *
 * @param allFeatureValues List of Filter objects from side menu filter
 * @param selectedPgOptions List of options selected at Product Group selector
 * @param appliedPgOptions List of options applied at Product Group selector
 * @param selectedSegmentOption Segment selector at Stacked Chart
 * @param setSelectedSegmentOption Segment selector setter at Stacked Chart
 * @param hasUserClickedApplyAtSideFilters true only if inside useEffect of pagesConfig.appliedFilter only
 */
const setUniqueSelectedSegmentOptionFromShowPerformanceWithin = (
  arrayObject: FeatureOption[],
  setSelectedSegmentOption: any,
  showPerformanceWithinOption: any,
): FeatureOption => {
  const filteredSelection = arrayObject.filter((index) => index.feature !== showPerformanceWithinOption.feature)[0];
  setSelectedSegmentOption(filteredSelection);
  return filteredSelection;
};

export const isSelectDiffPgFromAppliedFunction = (
  selectedPgOptions: Option[] | undefined,
  appliedPgOptions: Option[] | undefined,
): boolean => {
  const selectedPgs = selectedPgOptions ?? [];
  const appliedPgs = appliedPgOptions ?? [];

  if (selectedPgs.length === 0 && appliedPgs.length === 0) {
    return false;
  }

  // Check if there is any difference between selectedPgOptions and appliedPgOptions
  return (
    selectedPgs.length !== appliedPgs.length ||
    selectedPgs.some((selectedPg, idx) => appliedPgs[idx].value !== selectedPg.value)
  );
};

const hasDataTypeNonFeatureValidator = (hasDataTypeFeature: boolean, allDataTypeOptions: FeatureOption[]): boolean => {
  return !hasDataTypeFeature && allDataTypeOptions.length > 0;
};

const isFirstLoadOrAppliedDifferentPgAndClickApplyValidator = (
  isFirstLoad: boolean,
  isSelectDiffPgFromApplied: boolean,
  hasUserClickedApplyAtSideFilters: boolean,
): boolean => {
  return isFirstLoad || (isSelectDiffPgFromApplied && hasUserClickedApplyAtSideFilters);
};

const updateDefaultBySegmentOption = (
  isFirstLoadOrAppliedDifferentPgAndClickApply: boolean,
  isBySelectingFeatureAndHasDataTypeFeature: boolean,
  hasDataTypeNonFeature: boolean,
  setSelectedSegmentOption: any,
  dataTypeFeatureOptions: FeatureOption[],
  allDataTypeOptions: FeatureOption[],
): FeatureOption => {
  if (isFirstLoadOrAppliedDifferentPgAndClickApply) {
    if (isBySelectingFeatureAndHasDataTypeFeature) {
      setSelectedSegmentOption(dataTypeFeatureOptions[0]);
      return dataTypeFeatureOptions[0];
    } else if (hasDataTypeNonFeature) {
      setSelectedSegmentOption(allDataTypeOptions[0]);
      return allDataTypeOptions[0];
    }
  }
  return initialSegmentByValue;
};

const updateBySegmentSelectionIfSelectedOptionNotFoundInAllFeatureValues = (
  isBySelectingFeatureAndHasDataTypeFeature: boolean,
  allDataTypeOptions: FeatureOption[],
  dataTypeFeatureOptions: FeatureOption[],
  setSelectedSegmentOption: any,
  showPerformanceWithinOption: FeatureOption,
): FeatureOption => {
  if (isBySelectingFeatureAndHasDataTypeFeature) {
    return setUniqueSelectedSegmentOptionFromShowPerformanceWithin(
      dataTypeFeatureOptions,
      setSelectedSegmentOption,
      showPerformanceWithinOption,
    );
  }

  return setUniqueSelectedSegmentOptionFromShowPerformanceWithin(
    allDataTypeOptions,
    setSelectedSegmentOption,
    showPerformanceWithinOption,
  );
};

const bySegmentFilterHasAtMostOneOption = (
  allDataTypeOptions: FeatureOption[],
  setSelectedSegmentOption: any,
  showPerformanceWithin: FeatureOption,
) => {
  //First level check , prevent multiple loading error.
  if (allDataTypeOptions.length === 0) {
    return true;
  }

  // Direct return 'No options' when only one feature value
  if (
    allDataTypeOptions.length === 1 &&
    allDataTypeOptions.every((feature) => feature.value === showPerformanceWithin.feature)
  ) {
    setSelectedSegmentOption(initialSegmentByValue);
    return true;
  }

  return false;
};

export type HandleStackedChartSegmentSelectParams = {
  allFeatureValues: Filters[];
  selectedPgOptions: Option[] | undefined;
  appliedPgOptions: Option[] | undefined;
  selectedSegmentOption: FeatureOption;
  setSelectedSegmentOption: (f: FeatureOption) => void;
  showPerformanceWithinOption: FeatureOption;
  hasUserClickedApplyAtSideFilters: boolean;
  disabledFeatureValues?: FilterKeys[];
};

export const handleStackedChartSegmentSelect = ({
  allFeatureValues,
  selectedPgOptions,
  appliedPgOptions,
  selectedSegmentOption,
  setSelectedSegmentOption,
  showPerformanceWithinOption,
  hasUserClickedApplyAtSideFilters,
  disabledFeatureValues = [],
}: HandleStackedChartSegmentSelectParams): FeatureOption => {
  const filteredAllFeatureValues = allFeatureValues.filter(
    (filter) => !disabledFeatureValues.includes(filter.filterKey),
  );

  const allDataTypeOptions: FeatureOption[] = buildOptions(filteredAllFeatureValues);
  if (bySegmentFilterHasAtMostOneOption(allDataTypeOptions, setSelectedSegmentOption, showPerformanceWithinOption)) {
    return initialSegmentByValue;
  }

  const dataTypeFeatureOptions: FeatureOption[] = allDataTypeOptions.filter((featureValue) =>
    isDataTypeFeature(featureValue),
  );
  const hasDataTypeFeature: boolean = dataTypeFeatureOptions.length > 0;
  const hasDataTypeNonFeature: boolean = hasDataTypeNonFeatureValidator(hasDataTypeFeature, allDataTypeOptions);
  const isFirstLoad: boolean = selectedSegmentOption.dataType === '';
  const isPreviousSelectedDataTypeFeature: boolean = isDataTypeFeature(selectedSegmentOption);
  const isSelectDiffPgFromApplied: boolean = isSelectDiffPgFromAppliedFunction(selectedPgOptions, appliedPgOptions);

  const isBySelectingFeatureAndHasDataTypeFeature: boolean = hasDataTypeFeature && isPreviousSelectedDataTypeFeature;

  //Is show performance within selecting ADDFEAT or SEGFEAT
  const isShowPerformanceWithinPreviousSelectedDataTypeFeature: boolean =
    isDataTypeFeature(showPerformanceWithinOption);

  const isFirstLoadOrAppliedDifferentPgAndClickApply: boolean = isFirstLoadOrAppliedDifferentPgAndClickApplyValidator(
    isFirstLoad,
    isSelectDiffPgFromApplied,
    hasUserClickedApplyAtSideFilters,
  );

  const isSelectedByFeatureFoundInFilteredAllFeatureValues = filteredAllFeatureValues.some(
    (featureValue) => featureValue.feature === selectedSegmentOption.feature,
  );

  // Update default by segment selection only first load or (change pg + apply side filter)
  if (isFirstLoad) {
    return updateDefaultBySegmentOption(
      isFirstLoadOrAppliedDifferentPgAndClickApply,
      isBySelectingFeatureAndHasDataTypeFeature,
      hasDataTypeNonFeature,
      setSelectedSegmentOption,
      dataTypeFeatureOptions,
      allDataTypeOptions,
    );
  }

  //Set the SelectedSegmentOption to 1st available option. When selected timeframe don't have previously selected option
  //it should select the 1st available option in By dropdown
  if (!hasUserClickedApplyAtSideFilters && !isSelectDiffPgFromApplied) {
    if (isBySelectingFeatureAndHasDataTypeFeature && isShowPerformanceWithinPreviousSelectedDataTypeFeature) {
      //Special handling
      //If show performance within selected ADDFEAT or SEGFEAT, By Segment should cannot not select same option and should select second feature from available options.
      //If allDataTypeOptions has more than one ADDFEAT or SEGFEAT
      //Example: Show performance within select RESOLUTION as feature same as By segment, if this is the case By segment will auto select CAMCORDER TYPE as feature.
      //First condition added for multi-category (filter was resetting for single feature value)
      if (showPerformanceWithinOption.value !== selectedSegmentOption.value || dataTypeFeatureOptions.length > 1) {
        return setUniqueSelectedSegmentOptionFromShowPerformanceWithin(
          dataTypeFeatureOptions,
          setSelectedSegmentOption,
          showPerformanceWithinOption,
        );
      } else if (dataTypeFeatureOptions.length === 1) {
        //If user select feature from show performance within and only have one feature from that PG, should select first segment instead.
        //Example: If FoodPreparation has only one feature which is TYPE OF FOODPR,in this case By segment should select first segment instead of same feature.
        return setUniqueSelectedSegmentOptionFromShowPerformanceWithin(
          allDataTypeOptions,
          setSelectedSegmentOption,
          showPerformanceWithinOption,
        );
      }
    } else if (
      //Special handling - Case 1
      //No feature available from by dropdown,therefore select first segment instead.
      //Example: If show performance within selected ADDFEAT or SEGFEAT and that selected PG
      // only has one ADDFEAT or SEGFEAT, in this case By segment should select first segment instead feature.

      //Special handling - Case 2
      //By selector will auto select different segment with if By selected same segment with show performance within
      //Example: If show performance within selected Brand same to By segment selection,in this case, by segment should auto change to first segment instead of Brand.
      hasDataTypeNonFeature &&
      showPerformanceWithinOption.feature === selectedSegmentOption.feature
    ) {
      return setUniqueSelectedSegmentOptionFromShowPerformanceWithin(
        allDataTypeOptions,
        setSelectedSegmentOption,
        showPerformanceWithinOption,
      );
    } else if (!isSelectedByFeatureFoundInFilteredAllFeatureValues) {
      //Special handling
      //Update By segment option if selected feature or segment not found in allDataTypeOptions
      //Example: If By segment selecting feature or segment that not belong to current selected PG,
      //this function below will auto select first feature or segment option from available list base on the previous selection from by Segment.
      return updateBySegmentSelectionIfSelectedOptionNotFoundInAllFeatureValues(
        isBySelectingFeatureAndHasDataTypeFeature,
        allDataTypeOptions,
        dataTypeFeatureOptions,
        setSelectedSegmentOption,
        showPerformanceWithinOption,
      );
    }
  }
  return selectedSegmentOption;
};
